import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser, LoginInput, Permission } from '@aurea/contracts';
import { ApiRequestError, api } from './api';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
  /** Перечитать профиль: нужен после действий, меняющих его извне кабинета. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (error) {
        // 401 — обычное состояние «не вошёл», а не сбой: показываем форму входа.
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: api.login,
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  });

  const logoutMutation = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.setQueryData(['me'], null);
      // Кабинеты держат в кэше чужие данные — после выхода их быть не должно.
      queryClient.clear();
    },
  });

  const user = data ?? null;

  const can = useCallback(
    (permission: Permission) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login: loginMutation.mutateAsync,
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      can,
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['me'] });
      },
    }),
    [user, isLoading, loginMutation.mutateAsync, logoutMutation.mutateAsync, can, queryClient],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth вызван вне AuthProvider');
  return context;
}

/** Инициалы для аватара — как в прототипах: имя + фамилия. */
export function initials(user: Pick<CurrentUser, 'firstName' | 'lastName'>): string {
  return `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase();
}
